// eslint-disable-next-line no-unused-vars
import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { MaterialIcon } from '../../../MaterialIcon';
import { TextField } from '../../../TextField';
import { Button } from '../../../Button';

const FilterColumnPill = styled(Button).attrs({
    icon: 'close',
    iconProps: {
        color: '#fff',
        size: 18
    },
    iconPosition: 'right',
    size: 'sm'
})`
    border-radius: 2rem;
    margin-left: 5px;
    align-self: center;
    background: #fff;
    border: 1px solid #337ab7;
    color: #337ab7;
    white-space: nowrap;
`;

const FilterColumnPillSpan = styled.span`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 6rem;
`;

/**
 * This component is automatically rendered as part of the `<DataTable />`
 * header, and is directly related to the `setGlobalFilter` method of
 * `react-table`.
 */

export const DataTableFilter = forwardRef(
    (
        {
            id,
            onChange,
            filterColumn,
            setFilterColumn,
            showFilterColumnOptions,
            setShowFilterColumnOptions,
            clearable,
            ...props
        },
        ref
    ) => {
        const a11yProps = {};
        const filterInputID = `${id}--filter`;
        const inputRef = useRef(null);

        useImperativeHandle(
            ref,
            () => ({
                inputFocus: () => {
                    inputRef?.current?.focus();
                },
                clear: () => {
                    if (inputRef?.current?.value?.[0] === '#') {
                        inputRef.current.value = '';
                    }
                }
            }),
            [inputRef, inputRef?.current]
        );

        return (
            <TextField
                ref={inputRef}
                onChange={(e) => {
                    onChange(e.target.value);
                }}
                tabIndex="0"
                id={filterInputID}
                innerPrefix={
                    filterColumn ? (
                        <FilterColumnPill
                            onClick={() => {
                                setFilterColumn(null);
                                onChange(inputRef?.current?.value || '');
                            }}
                        >
                            in:<FilterColumnPillSpan>{filterColumn.label}</FilterColumnPillSpan>
                        </FilterColumnPill>
                    ) : null
                }
                inputProps={{
                    prefix: <MaterialIcon icon="search" intent="muted" size={18} />,
                    suffix: clearable ? <MaterialIcon icon={`close`} intent={`muted`} size={18} /> : null,
                    suffixOnClick: () => {
                        if (inputRef?.current) {
                            inputRef.current.value = '';
                            onChange(inputRef.current.value);
                        }
                    },
                    tabIndex: '0',
                    style: {
                        flexBasis: `33.337%`
                    }
                }}
                autocomplete="false"
                onFocus={(e) => {
                    if (e.target.value?.[0] === '#' && !showFilterColumnOptions) {
                        setShowFilterColumnOptions(true);
                    }
                }}
                onKeyDown={(e) => {
                    // Detect backspace when field is empty but with filter column set
                    if (e.keyCode === 8 && !e.target.value && filterColumn) {
                        setFilterColumn(null);
                    }
                }}
                name={`${id}--filter-field`}
                size={`sm`}
                {...a11yProps}
                {...props}
            />
        );
    }
);

DataTableFilter.displayName = 'DataTableFilter';
DataTableFilter.propTypes = {
    /**
     * This is set by `<DataTable />` automatically.
     */
    id: PropTypes.string.isRequired,
    /**
     * This is set by `<DataTable />` automatically.
     */
    onChange: PropTypes.func.isRequired,
    /**
     * This is set by `<DataTable />` automatically.
     */
    placeholder: PropTypes.string.isRequired,
    filterColumn: PropTypes.object,
    setFilterColumn: PropTypes.func,
    showFilterColumnOptions: PropTypes.bool,
    setShowFilterColumnOptions: PropTypes.func,
    clearable: PropTypes.bool
};

DataTableFilter.defaultProps = {};
